import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { issuesApi, issueTypeConfigsApi, type IssueStatus, type IssueType, type IssuePriority } from "../../api/issues.api";
import { useAuthStore } from "../../store/auth.store";

const ACTIVE_STATUSES: IssueStatus[] = ["OPEN", "IN_PROGRESS", "UNDER_REVIEW"];

const STATUS_CLASS: Record<IssueStatus, string> = {
  OPEN: "success",
  IN_PROGRESS: "warning",
  UNDER_REVIEW: "warning",
  RESOLVED: "muted",
  CLOSED: "muted",
  WONT_FIX: "muted",
  ARCHIVED: "muted",
};

const STATUS_LABEL: Record<IssueStatus, string> = {
  OPEN: "Open",
  IN_PROGRESS: "In Progress",
  UNDER_REVIEW: "Under Review",
  RESOLVED: "Resolved",
  CLOSED: "Closed",
  WONT_FIX: "Won't Fix",
  ARCHIVED: "Archived",
};

const PRIORITY_CLASS: Record<IssuePriority, string> = {
  LOW: "muted",
  MEDIUM: "warning",
  HIGH: "danger",
  CRITICAL: "danger",
};

function StatusBadge({ status }: { status: IssueStatus }) {
  return <span className={`badge ${STATUS_CLASS[status]}`}>{STATUS_LABEL[status]}</span>;
}

function PriorityBadge({ priority }: { priority: IssuePriority }) {
  return <span className={`badge ${PRIORITY_CLASS[priority]}`}>{priority}</span>;
}

export function IssueList() {
  const isAdmin = useAuthStore((s) => s.user?.isPanelAdmin === true);
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState<IssueStatus | "">("");
  const [typeFilter, setTypeFilter] = useState<IssueType | "">("");
  const [priorityFilter, setPriorityFilter] = useState<IssuePriority | "">("");
  const [activeOnly, setActiveOnly] = useState(true);

  const { data: typeConfigs } = useQuery({
    queryKey: ["issue-type-configs"],
    queryFn: issueTypeConfigsApi.list,
  });

  const typeLabelMap: Record<string, string> = Object.fromEntries(
    (typeConfigs ?? []).map((c) => [c.type, c.label])
  );

  const params = {
    page,
    limit: 20,
    ...(statusFilter
      ? { status: statusFilter }
      : activeOnly ? { statuses: ACTIVE_STATUSES } : {}),
    ...(typeFilter && { type: typeFilter }),
    ...(priorityFilter && { priority: priorityFilter }),
    ...(isAdmin && !activeOnly && { includeArchived: true }),
  };

  const { data, isLoading } = useQuery({
    queryKey: ["issues", params],
    queryFn: () => issuesApi.list(params),
  });

  const totalPages = data ? Math.ceil(data.total / data.limit) : 1;

  return (
    <>
      <div className="page-header">
        <h2>Report App Issues</h2>
        <Link to="/issues/new">
          <button className="primary">Report an Issue</button>
        </Link>
      </div>

      {isAdmin && (
        <div style={{ display: "flex", gap: 8, marginBottom: 16, flexWrap: "wrap" }}>
          <select
            value={statusFilter}
            onChange={(e) => { setStatusFilter(e.target.value as IssueStatus | ""); setPage(1); }}
            style={{ minWidth: 140 }}
          >
            <option value="">All statuses</option>
            {(Object.keys(STATUS_LABEL) as IssueStatus[]).map((s) => (
              <option key={s} value={s}>{STATUS_LABEL[s]}</option>
            ))}
          </select>
          <select
            value={typeFilter}
            onChange={(e) => { setTypeFilter(e.target.value as IssueType | ""); setPage(1); }}
            style={{ minWidth: 140 }}
          >
            <option value="">All types</option>
            {(typeConfigs ?? []).map((cfg) => (
              <option key={cfg.type} value={cfg.type}>{cfg.label}</option>
            ))}
          </select>
          <select
            value={priorityFilter}
            onChange={(e) => { setPriorityFilter(e.target.value as IssuePriority | ""); setPage(1); }}
            style={{ minWidth: 140 }}
          >
            <option value="">All priorities</option>
            <option value="LOW">Low</option>
            <option value="MEDIUM">Medium</option>
            <option value="HIGH">High</option>
            <option value="CRITICAL">Critical</option>
          </select>
          <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, cursor: "pointer", whiteSpace: "nowrap" }}>
            <input
              type="checkbox"
              checked={activeOnly}
              onChange={(e) => { setActiveOnly(e.target.checked); setStatusFilter(""); setPage(1); }}
            />
            Active Issues Only
          </label>
        </div>
      )}

      {isLoading && <p className="muted">Loading…</p>}

      {data && data.issues.length === 0 && (
        <div className="card">
          <p className="muted">
            {isAdmin ? "No issues found." : "You haven't reported any issues yet."}
          </p>
        </div>
      )}

      {data && data.issues.length > 0 && (
        <div className="card table-scroll" style={{ padding: 0 }}>
          <table>
            <thead>
              <tr>
                <th>Title</th>
                <th>Type</th>
                {isAdmin && <th>Submitted by</th>}
                {isAdmin && <th>Assigned To</th>}
                {isAdmin && <th>Priority</th>}
                <th>Status</th>
                <th>Date</th>
              </tr>
            </thead>
            <tbody>
              {data.issues.map((issue) => (
                <tr key={issue.id}>
                  <td>
                    <Link to={`/issues/${issue.id}`}>{issue.title}</Link>
                    {issue.app && (
                      <span className="muted" style={{ fontSize: 12, marginLeft: 8 }}>
                        {issue.app.name}
                      </span>
                    )}
                  </td>
                  <td className="muted">{typeLabelMap[issue.type] ?? issue.type}</td>
                  {isAdmin && (
                    <td className="muted">
                      {issue.submittedBy.displayName ?? issue.submittedBy.email}
                    </td>
                  )}
                  {isAdmin && (
                    <td className="muted">
                      {issue.assignedTo
                        ? (issue.assignedTo.displayName ?? issue.assignedTo.email)
                        : "Unassigned"}
                    </td>
                  )}
                  {isAdmin && (
                    <td><PriorityBadge priority={issue.priority} /></td>
                  )}
                  <td><StatusBadge status={issue.status} /></td>
                  <td className="muted">{new Date(issue.createdAt).toLocaleDateString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {totalPages > 1 && (
        <div style={{ marginTop: 16, display: "flex", gap: 8 }}>
          <button disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>← Prev</button>
          <span className="muted" style={{ lineHeight: "32px" }}>
            Page {page} of {totalPages}
          </span>
          <button disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>Next →</button>
        </div>
      )}
    </>
  );
}
